import React, { useEffect } from 'react';

const OAuthJWT = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'OAuth 2.0 & JWT - Modern Authentication & Authorization 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master OAuth 2.0 and JWT for secure authentication and authorization. Learn implementation patterns, security best practices, and token management.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master OAuth 2.0 and JWT for secure authentication and authorization. Learn implementation patterns, security best practices, and token management.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>OAuth 2.0 & JWT: Modern Authentication & Authorization</h1>
        <p style={styles.subtitle}>
          Secure user identity and session management with industry-standard protocols
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Authentication & Authorization</h2>
          <p style={styles.paragraph}>
            Authentication and authorization are fundamental security concepts in modern applications. 
            Authentication verifies who a user is, while authorization determines what they can access. 
            OAuth 2.0 and JWT (JSON Web Tokens) have become the industry standards for implementing 
            secure, scalable authentication and authorization systems.
          </p>
          <p style={styles.paragraph}>
            OAuth 2.0 provides a framework for authorization that allows third-party applications to 
            access user resources without exposing credentials. JWT offers a compact, URL-safe means 
            of representing claims between parties. Together, they enable secure, stateless authentication 
            that scales across distributed systems.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>OAuth 2.0 Overview</h2>
          
          <h3 style={styles.heading}>Core Concepts</h3>
          <p style={styles.paragraph}>
            OAuth 2.0 is an authorization framework that enables applications to obtain limited access 
            to user accounts. It works by delegating user authentication to the service that hosts the 
            user account and authorizing third-party applications to access that account.
          </p>
          <pre style={styles.codeBlock}>
{`OAuth 2.0 Key Components:

1. Resource Owner (User):
   - The entity that can grant access to a protected resource
   - Typically the end-user

2. Client (Application):
   - The application requesting access to protected resources
   - Can be web app, mobile app, or server-to-server

3. Authorization Server:
   - Issues access tokens after authenticating the resource owner
   - Examples: Google, Facebook, Auth0, Okta

4. Resource Server:
   - Hosts protected resources
   - Accepts and validates access tokens
   - Often the same as authorization server

OAuth 2.0 Grant Types:

1. Authorization Code Grant (Most Secure):
   - For web applications with server-side code
   - Involves redirect to authorization server
   - Exchange authorization code for access token

2. Client Credentials Grant:
   - For server-to-server authentication
   - No user interaction required
   - Client authenticates with its own credentials

3. Resource Owner Password Credentials Grant:
   - For trusted first-party applications
   - User provides username/password directly
   - Generally discouraged for security reasons

4. Implicit Grant (Deprecated):
   - For single-page applications (SPAs)
   - Tokens returned directly in URL fragment
   - Replaced by Authorization Code + PKCE

5. Authorization Code + PKCE:
   - Secure version for SPAs and mobile apps
   - Proof Key for Code Exchange prevents attacks
   - Current best practice for public clients

OAuth 2.0 Token Types:

Access Token:
- Short-lived (15 minutes to 1 hour)
- Used to access protected resources
- Can be opaque or JWT format
- Should be treated as opaque by clients

Refresh Token:
- Long-lived (days to months)
- Used to obtain new access tokens
- More sensitive than access tokens
- Should be stored securely

ID Token (OpenID Connect):
- Contains user identity information
- Always in JWT format
- Used for authentication (not authorization)
- Contains claims about the user`}
          </pre>

          <h3 style={styles.heading}>OAuth 2.0 Flow Examples</h3>
          <p style={styles.paragraph}>
            Different OAuth 2.0 flows are designed for different types of applications and security 
            requirements. Understanding when and how to use each flow is crucial for secure implementation.
          </p>
          <pre style={styles.codeBlock}>
{`Authorization Code Flow (Web Applications):

Step 1: Client redirects user to authorization server
GET https://auth.example.com/oauth/authorize?
  response_type=code&
  client_id=12345&
  redirect_uri=https://myapp.com/callback&
  scope=read:profile write:posts&
  state=random_state_value

Step 2: User authenticates and grants permission
- User logs in to authorization server
- User sees consent screen for requested permissions
- User approves or denies the request

Step 3: Authorization server redirects back with code
GET https://myapp.com/callback?
  code=authorization_code_here&
  state=random_state_value

Step 4: Client exchanges code for tokens
POST https://auth.example.com/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=authorization_code&
code=authorization_code_here&
client_id=12345&
client_secret=client_secret_here&
redirect_uri=https://myapp.com/callback

Step 5: Authorization server responds with tokens
{
  "access_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "refresh_token": "def50200a1b2c3d4e5f6...",
  "scope": "read:profile write:posts"
}

Client Credentials Flow (Server-to-Server):

Step 1: Client authenticates with authorization server
POST https://auth.example.com/oauth/token
Content-Type: application/x-www-form-urlencoded
Authorization: Basic base64(client_id:client_secret)

grant_type=client_credentials&
scope=api:read api:write

Step 2: Authorization server responds with access token
{
  "access_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "api:read api:write"
}

Authorization Code + PKCE Flow (SPAs/Mobile):

Step 1: Generate PKCE parameters
code_verifier = base64url(random(32))  // 43-128 characters
code_challenge = base64url(sha256(code_verifier))
code_challenge_method = "S256"

Step 2: Redirect to authorization server
GET https://auth.example.com/oauth/authorize?
  response_type=code&
  client_id=12345&
  redirect_uri=https://spa.example.com/callback&
  scope=openid profile email&
  state=random_state&
  code_challenge=code_challenge_here&
  code_challenge_method=S256

Step 3: Exchange code for tokens (with PKCE)
POST https://auth.example.com/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=authorization_code&
code=authorization_code_here&
client_id=12345&
redirect_uri=https://spa.example.com/callback&
code_verifier=original_code_verifier

Using Access Tokens:

GET https://api.example.com/user/profile
Authorization: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...

Refreshing Access Tokens:

POST https://auth.example.com/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=refresh_token&
refresh_token=def50200a1b2c3d4e5f6...&
client_id=12345&
client_secret=client_secret_here`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>JSON Web Tokens (JWT)</h2>
          
          <h3 style={styles.heading}>JWT Structure and Format</h3>
          <p style={styles.paragraph}>
            JSON Web Tokens are compact, URL-safe tokens that contain JSON-encoded claims. They consist 
            of three parts separated by dots: header, payload, and signature. JWTs can be signed and 
            optionally encrypted for secure transmission of information.
          </p>
          <pre style={styles.codeBlock}>
{`JWT Structure:

Format: header.payload.signature

Example JWT:
eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c

1. Header (Base64URL encoded):
{
  "alg": "HS256",    // Algorithm used for signing
  "typ": "JWT"       // Token type
}

2. Payload (Base64URL encoded):
{
  "sub": "1234567890",           // Subject (user ID)
  "name": "John Doe",            // Custom claim
  "iat": 1516239022,             // Issued at timestamp
  "exp": 1516242622,             // Expiration timestamp
  "aud": "my-api",               // Audience
  "iss": "https://auth.example.com", // Issuer
  "jti": "unique-token-id",      // JWT ID
  "scope": "read:profile write:posts" // Permissions
}

3. Signature:
HMACSHA256(
  base64UrlEncode(header) + "." + base64UrlEncode(payload),
  secret
)

Standard Claims:

Registered Claims (RFC 7519):
- iss (issuer): Token issuer
- sub (subject): Token subject (usually user ID)
- aud (audience): Token audience
- exp (expiration): Expiration timestamp
- nbf (not before): Not valid before timestamp
- iat (issued at): Issued timestamp
- jti (JWT ID): Unique token identifier

Public Claims:
- Defined in IANA registry or collision-resistant namespace
- Examples: email, name, roles

Private Claims:
- Custom claims for specific applications
- Examples: user_id, permissions, tenant_id

JWT Algorithms:

Symmetric (HMAC):
- HS256 (HMAC SHA-256)
- HS384 (HMAC SHA-384)
- HS512 (HMAC SHA-512)
- Same secret for signing and verification

Asymmetric (RSA/ECDSA):
- RS256 (RSA SHA-256)
- RS384 (RSA SHA-384)
- RS512 (RSA SHA-512)
- ES256 (ECDSA SHA-256)
- Private key for signing, public key for verification

None Algorithm:
- No signature (unsecured JWT)
- Should be explicitly disabled in production`}
          </pre>

          <h3 style={styles.heading}>JWT Implementation</h3>
          <p style={styles.paragraph}>
            Implementing JWT requires careful attention to security, token validation, and proper key 
            management. Here are comprehensive examples for creating and validating JWTs.
          </p>
          <pre style={styles.codeBlock}>
{`JWT Implementation Examples:

# Python JWT Implementation
import jwt
import datetime
import hashlib
import hmac
from cryptography.hazmat.primitives import hashes
from cryptography.hazmat.primitives.asymmetric import rsa, padding
from cryptography.hazmat.primitives import serialization

class JWTManager:
    def __init__(self, secret_key: str = None, private_key: str = None, public_key: str = None):
        self.secret_key = secret_key
        self.private_key = private_key
        self.public_key = public_key
        self.algorithm = "RS256" if private_key else "HS256"
    
    def create_access_token(self, user_id: str, email: str, roles: list, 
                          expires_in: int = 3600) -> str:
        """Create access token with user claims"""
        now = datetime.datetime.utcnow()
        payload = {
            # Standard claims
            "iss": "https://myapp.com",
            "sub": user_id,
            "aud": "my-api",
            "iat": int(now.timestamp()),
            "exp": int((now + datetime.timedelta(seconds=expires_in)).timestamp()),
            "jti": self._generate_jti(),
            
            # Custom claims
            "email": email,
            "roles": roles,
            "token_type": "access"
        }
        
        signing_key = self.private_key if self.algorithm.startswith('RS') else self.secret_key
        return jwt.encode(payload, signing_key, algorithm=self.algorithm)
    
    def create_refresh_token(self, user_id: str, expires_in: int = 2592000) -> str:
        """Create refresh token (30 days default)"""
        now = datetime.datetime.utcnow()
        payload = {
            "iss": "https://myapp.com",
            "sub": user_id,
            "iat": int(now.timestamp()),
            "exp": int((now + datetime.timedelta(seconds=expires_in)).timestamp()),
            "jti": self._generate_jti(),
            "token_type": "refresh"
        }
        
        signing_key = self.private_key if self.algorithm.startswith('RS') else self.secret_key
        return jwt.encode(payload, signing_key, algorithm=self.algorithm)
    
    def verify_token(self, token: str, token_type: str = "access") -> dict:
        """Verify and decode JWT token"""
        try:
            verification_key = self.public_key if self.algorithm.startswith('RS') else self.secret_key
            
            payload = jwt.decode(
                token,
                verification_key,
                algorithms=[self.algorithm],
                audience="my-api",
                issuer="https://myapp.com"
            )
            
            # Verify token type
            if payload.get("token_type") != token_type:
                raise jwt.InvalidTokenError(f"Invalid token type: expected {token_type}")
            
            return payload
            
        except jwt.ExpiredSignatureError:
            raise Exception("Token has expired")
        except jwt.InvalidTokenError as e:
            raise Exception(f"Invalid token: {str(e)}")
    
    def refresh_access_token(self, refresh_token: str) -> tuple:
        """Create new access token from refresh token"""
        try:
            payload = self.verify_token(refresh_token, "refresh")
            user_id = payload["sub"]
            
            # In real implementation, fetch user data from database
            user_data = self._get_user_data(user_id)
            
            new_access_token = self.create_access_token(
                user_id=user_id,
                email=user_data["email"],
                roles=user_data["roles"]
            )
            
            # Optionally rotate refresh token
            new_refresh_token = self.create_refresh_token(user_id)
            
            return new_access_token, new_refresh_token
            
        except Exception as e:
            raise Exception(f"Failed to refresh token: {str(e)}")
    
    def _generate_jti(self) -> str:
        """Generate unique JWT ID"""
        import uuid
        return str(uuid.uuid4())
    
    def _get_user_data(self, user_id: str) -> dict:
        """Fetch user data from database"""
        # Placeholder - implement database lookup
        return {
            "email": "user@example.com",
            "roles": ["user", "admin"]
        }

# Usage Example
jwt_manager = JWTManager(secret_key="your-secret-key")

# Create tokens
access_token = jwt_manager.create_access_token(
    user_id="12345",
    email="john@example.com",
    roles=["user", "admin"]
)

refresh_token = jwt_manager.create_refresh_token("12345")

# Verify token
try:
    payload = jwt_manager.verify_token(access_token)
    print(f"Token valid for user: {payload['email']}")
    print(f"Roles: {payload['roles']}")
except Exception as e:
    print(f"Token validation failed: {e}")

# Refresh tokens
try:
    new_access, new_refresh = jwt_manager.refresh_access_token(refresh_token)
    print("Tokens refreshed successfully")
except Exception as e:
    print(f"Token refresh failed: {e}")

# Express.js JWT Middleware
const jwt = require('jsonwebtoken');
const { promisify } = require('util');

class JWTService {
    constructor(secretKey, publicKey = null, privateKey = null) {
        this.secretKey = secretKey;
        this.publicKey = publicKey;
        this.privateKey = privateKey;
        this.algorithm = privateKey ? 'RS256' : 'HS256';
    }

    createAccessToken(userId, email, roles, expiresIn = '1h') {
        const payload = {
            iss: 'https://myapp.com',
            sub: userId,
            aud: 'my-api',
            iat: Math.floor(Date.now() / 1000),
            email: email,
            roles: roles,
            token_type: 'access'
        };

        const signingKey = this.privateKey || this.secretKey;
        return jwt.sign(payload, signingKey, {
            algorithm: this.algorithm,
            expiresIn: expiresIn
        });
    }

    createRefreshToken(userId, expiresIn = '30d') {
        const payload = {
            iss: 'https://myapp.com',
            sub: userId,
            iat: Math.floor(Date.now() / 1000),
            token_type: 'refresh'
        };

        const signingKey = this.privateKey || this.secretKey;
        return jwt.sign(payload, signingKey, {
            algorithm: this.algorithm,
            expiresIn: expiresIn
        });
    }

    async verifyToken(token, tokenType = 'access') {
        try {
            const verificationKey = this.publicKey || this.secretKey;
            
            const payload = await promisify(jwt.verify)(token, verificationKey, {
                algorithms: [this.algorithm],
                audience: 'my-api',
                issuer: 'https://myapp.com'
            });

            if (payload.token_type !== tokenType) {
                throw new Error(\`Invalid token type: expected $\{tokenType\}\`);
            }

            return payload;
        } catch (error) {
            throw new Error(\`Token verification failed: $\{error.message\}\`);
        }
    }
}

// Middleware for protecting routes
function authenticateToken(jwtService) {
    return async (req, res, next) => {
        const authHeader = req.headers['authorization'];
        const token = authHeader && authHeader.split(' ')[1]; // Bearer TOKEN

        if (!token) {
            return res.status(401).json({ error: 'Access token required' });
        }

        try {
            const payload = await jwtService.verifyToken(token);
            req.user = {
                id: payload.sub,
                email: payload.email,
                roles: payload.roles
            };
            next();
        } catch (error) {
            return res.status(403).json({ error: error.message });
        }
    };
}

// Role-based authorization middleware
function requireRole(role) {
    return (req, res, next) => {
        if (!req.user || !req.user.roles.includes(role)) {
            return res.status(403).json({ error: 'Insufficient permissions' });
        }
        next();
    };
}

// Usage in Express routes
const express = require('express');
const app = express();
const jwtService = new JWTService('your-secret-key');

// Login endpoint
app.post('/auth/login', async (req, res) => {
    const { email, password } = req.body;
    
    // Validate credentials (implement your logic)
    const user = await validateCredentials(email, password);
    if (!user) {
        return res.status(401).json({ error: 'Invalid credentials' });
    }

    // Create tokens
    const accessToken = jwtService.createAccessToken(
        user.id, user.email, user.roles
    );
    const refreshToken = jwtService.createRefreshToken(user.id);

    res.json({
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: 'Bearer',
        expires_in: 3600
    });
});

// Protected route
app.get('/api/profile', 
    authenticateToken(jwtService),
    (req, res) => {
        res.json({
            user_id: req.user.id,
            email: req.user.email,
            roles: req.user.roles
        });
    }
);

// Admin-only route
app.get('/api/admin/users',
    authenticateToken(jwtService),
    requireRole('admin'),
    (req, res) => {
        // Admin functionality
        res.json({ message: 'Admin access granted' });
    }
);

// Token refresh endpoint
app.post('/auth/refresh', async (req, res) => {
    const { refresh_token } = req.body;
    
    if (!refresh_token) {
        return res.status(401).json({ error: 'Refresh token required' });
    }

    try {
        const payload = await jwtService.verifyToken(refresh_token, 'refresh');
        const userId = payload.sub;
        
        // Fetch user data
        const user = await getUserById(userId);
        
        // Create new access token
        const newAccessToken = jwtService.createAccessToken(
            user.id, user.email, user.roles
        );

        res.json({
            access_token: newAccessToken,
            token_type: 'Bearer',
            expires_in: 3600
        });
    } catch (error) {
        res.status(403).json({ error: error.message });
    }
});`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Security Best Practices</h2>
          
          <h3 style={styles.heading}>OAuth 2.0 Security</h3>
          <p style={styles.paragraph}>
            OAuth 2.0 security requires careful implementation to prevent common attacks and 
            vulnerabilities. Following security best practices is essential for protecting user data 
            and maintaining system integrity.
          </p>
          <pre style={styles.codeBlock}>
{`OAuth 2.0 Security Best Practices:

1. Use HTTPS Everywhere:
   - All OAuth endpoints must use HTTPS
   - Redirect URIs must use HTTPS (except localhost for development)
   - Never transmit tokens over HTTP

2. Validate Redirect URIs:
   - Register exact redirect URIs with authorization server
   - Use exact string matching (no wildcards)
   - Validate redirect_uri parameter in authorization requests

# Secure redirect URI validation
ALLOWED_REDIRECT_URIS = [
    'https://myapp.com/callback',
    'https://staging.myapp.com/callback',
    'http://localhost:3000/callback'  # Development only
]

def validate_redirect_uri(redirect_uri, client_id):
    client_config = get_client_config(client_id)
    if redirect_uri not in client_config['allowed_redirect_uris']:
        raise ValueError('Invalid redirect URI')
    return True

3. Implement State Parameter:
   - Use cryptographically random state values
   - Bind state to user session
   - Validate state parameter on callback

import secrets
import hashlib

def generate_state(session_id):
    random_value = secrets.token_urlsafe(32)
    state = hashlib.sha256(f"{session_id}:{random_value}".encode()).hexdigest()
    # Store state in session or database
    store_state(session_id, state)
    return state

def validate_state(session_id, received_state):
    stored_state = get_stored_state(session_id)
    if not stored_state or stored_state != received_state:
        raise ValueError('Invalid state parameter')
    # Remove used state
    remove_state(session_id)
    return True

4. Use PKCE for Public Clients:
   - Always use PKCE for SPAs and mobile apps
   - Generate cryptographically secure code verifier
   - Use S256 code challenge method

import base64
import hashlib
import secrets

def generate_pkce_pair():
    # Generate code verifier (43-128 characters)
    code_verifier = base64.urlsafe_b64encode(
        secrets.token_bytes(32)
    ).decode('utf-8').rstrip('=')
    
    # Generate code challenge
    code_challenge = base64.urlsafe_b64encode(
        hashlib.sha256(code_verifier.encode()).digest()
    ).decode('utf-8').rstrip('=')
    
    return code_verifier, code_challenge

5. Secure Token Storage:
   - Store tokens securely on client side
   - Use httpOnly cookies for web applications
   - Use secure storage on mobile devices
   - Never store tokens in localStorage for sensitive apps

// Secure token storage in browser
class SecureTokenStorage {
    static setTokens(accessToken, refreshToken) {
        // Store access token in memory (short-lived)
        this.accessToken = accessToken;
        
        // Store refresh token in httpOnly cookie
        document.cookie = \`refresh_token=$\{refreshToken\}; HttpOnly; Secure; SameSite=Strict; Max-Age=2592000\`;
    }
    
    static getAccessToken() {
        return this.accessToken;
    }
    
    static clearTokens() {
        this.accessToken = null;
        document.cookie = 'refresh_token=; HttpOnly; Secure; SameSite=Strict; Max-Age=0';
    }
}

6. Implement Token Rotation:
   - Rotate refresh tokens on each use
   - Invalidate old refresh tokens
   - Detect token reuse attacks

def refresh_tokens(refresh_token):
    try:
        # Verify refresh token
        payload = verify_refresh_token(refresh_token)
        user_id = payload['sub']
        
        # Check if token was already used
        if is_token_used(refresh_token):
            # Possible token reuse attack
            revoke_all_user_tokens(user_id)
            raise SecurityError('Token reuse detected')
        
        # Mark token as used
        mark_token_used(refresh_token)
        
        # Create new tokens
        new_access_token = create_access_token(user_id)
        new_refresh_token = create_refresh_token(user_id)
        
        # Revoke old refresh token
        revoke_token(refresh_token)
        
        return new_access_token, new_refresh_token
        
    except Exception as e:
        # Revoke token on any error
        revoke_token(refresh_token)
        raise e

7. Scope Validation:
   - Implement principle of least privilege
   - Validate scopes on resource server
   - Use granular scopes

SCOPE_PERMISSIONS = {
    'read:profile': ['GET /api/user/profile'],
    'write:profile': ['PUT /api/user/profile', 'PATCH /api/user/profile'],
    'read:posts': ['GET /api/posts', 'GET /api/posts/:id'],
    'write:posts': ['POST /api/posts', 'PUT /api/posts/:id', 'DELETE /api/posts/:id'],
    'admin:users': ['GET /api/admin/users', 'POST /api/admin/users', 'DELETE /api/admin/users/:id']
}

def check_scope_permission(token_scopes, required_permission):
    for scope in token_scopes:
        if required_permission in SCOPE_PERMISSIONS.get(scope, []):
            return True
    return False

8. Rate Limiting:
   - Implement rate limiting on OAuth endpoints
   - Prevent brute force attacks
   - Use progressive delays

from collections import defaultdict
import time

class RateLimiter:
    def __init__(self):
        self.attempts = defaultdict(list)
        self.blocked = defaultdict(float)
    
    def is_allowed(self, client_id, endpoint):
        now = time.time()
        
        # Check if client is blocked
        if client_id in self.blocked and now < self.blocked[client_id]:
            return False
        
        # Clean old attempts
        key = f"{client_id}:{endpoint}"
        self.attempts[key] = [
            attempt for attempt in self.attempts[key]
            if now - attempt < 3600  # 1 hour window
        ]
        
        # Check rate limit
        if len(self.attempts[key]) >= 10:  # 10 attempts per hour
            # Block for increasing duration
            block_duration = min(3600 * (2 ** (len(self.attempts[key]) - 10)), 86400)
            self.blocked[client_id] = now + block_duration
            return False
        
        # Record attempt
        self.attempts[key].append(now)
        return True`}
          </pre>

          <h3 style={styles.heading}>JWT Security</h3>
          <p style={styles.paragraph}>
            JWT security involves proper algorithm selection, key management, token validation, and 
            protection against common attacks. Implementing these practices ensures JWT tokens remain 
            secure throughout their lifecycle.
          </p>
          <pre style={styles.codeBlock}>
{`JWT Security Best Practices:

1. Algorithm Security:
   - Never use "none" algorithm in production
   - Prefer RS256 over HS256 for public/private key scenarios
   - Explicitly specify allowed algorithms

# Secure JWT verification
ALLOWED_ALGORITHMS = ['RS256', 'RS384', 'RS512']

def verify_jwt_secure(token, public_key):
    try:
        # Decode header to check algorithm
        header = jwt.get_unverified_header(token)
        algorithm = header.get('alg')
        
        # Reject "none" algorithm
        if algorithm == 'none':
            raise jwt.InvalidAlgorithmError('None algorithm not allowed')
        
        # Verify algorithm is in allowed list
        if algorithm not in ALLOWED_ALGORITHMS:
            raise jwt.InvalidAlgorithmError(f'Algorithm {algorithm} not allowed')
        
        # Verify token
        payload = jwt.decode(
            token,
            public_key,
            algorithms=ALLOWED_ALGORITHMS,
            options={
                'verify_signature': True,
                'verify_exp': True,
                'verify_nbf': True,
                'verify_iat': True,
                'verify_aud': True,
                'verify_iss': True
            }
        )
        
        return payload
        
    except jwt.InvalidTokenError as e:
        raise SecurityError(f'JWT validation failed: {str(e)}')

2. Key Management:
   - Use strong, randomly generated keys
   - Rotate keys regularly
   - Store keys securely
   - Use different keys for different environments

import secrets
from cryptography.hazmat.primitives.asymmetric import rsa
from cryptography.hazmat.primitives import serialization

class KeyManager:
    @staticmethod
    def generate_hmac_key(length=32):
        """Generate secure HMAC key"""
        return secrets.token_bytes(length)
    
    @staticmethod
    def generate_rsa_keypair(key_size=2048):
        """Generate RSA key pair"""
        private_key = rsa.generate_private_key(
            public_exponent=65537,
            key_size=key_size
        )
        
        private_pem = private_key.private_bytes(
            encoding=serialization.Encoding.PEM,
            format=serialization.PrivateFormat.PKCS8,
            encryption_algorithm=serialization.NoEncryption()
        )
        
        public_key = private_key.public_key()
        public_pem = public_key.public_bytes(
            encoding=serialization.Encoding.PEM,
            format=serialization.PublicFormat.SubjectPublicKeyInfo
        )
        
        return private_pem, public_pem
    
    @staticmethod
    def rotate_keys():
        """Implement key rotation"""
        # Generate new key pair
        new_private, new_public = KeyManager.generate_rsa_keypair()
        
        # Store new keys with version
        key_version = get_next_key_version()
        store_key(f'private_key_v{key_version}', new_private)
        store_key(f'public_key_v{key_version}', new_public)
        
        # Update current key version
        set_current_key_version(key_version)
        
        # Schedule old key cleanup (after grace period)
        schedule_key_cleanup(key_version - 1, delay_hours=24)

3. Token Validation:
   - Validate all claims (exp, nbf, iat, aud, iss)
   - Implement token blacklisting
   - Check token revocation

class JWTValidator:
    def __init__(self, issuer, audience):
        self.issuer = issuer
        self.audience = audience
        self.blacklist = TokenBlacklist()
    
    def validate_token(self, token, public_key):
        try:
            # Check if token is blacklisted
            if self.blacklist.is_blacklisted(token):
                raise jwt.InvalidTokenError('Token is blacklisted')
            
            # Decode and validate
            payload = jwt.decode(
                token,
                public_key,
                algorithms=['RS256'],
                audience=self.audience,
                issuer=self.issuer,
                options={
                    'verify_exp': True,
                    'verify_nbf': True,
                    'verify_iat': True,
                    'verify_aud': True,
                    'verify_iss': True,
                    'require_exp': True,
                    'require_iat': True
                }
            )
            
            # Additional custom validations
            self._validate_custom_claims(payload)
            
            return payload
            
        except jwt.ExpiredSignatureError:
            raise SecurityError('Token has expired')
        except jwt.InvalidAudienceError:
            raise SecurityError('Invalid token audience')
        except jwt.InvalidIssuerError:
            raise SecurityError('Invalid token issuer')
        except jwt.InvalidTokenError as e:
            raise SecurityError(f'Invalid token: {str(e)}')
    
    def _validate_custom_claims(self, payload):
        """Validate custom claims"""
        # Check token type
        if payload.get('token_type') not in ['access', 'refresh']:
            raise jwt.InvalidTokenError('Invalid token type')
        
        # Check issued at time (not too old)
        iat = payload.get('iat')
        if iat and time.time() - iat > 2592000:  # 30 days
            raise jwt.InvalidTokenError('Token too old')
        
        # Validate user still exists
        user_id = payload.get('sub')
        if not self._user_exists(user_id):
            raise jwt.InvalidTokenError('User no longer exists')

4. Token Blacklisting:
   - Implement efficient blacklist storage
   - Clean up expired tokens
   - Handle distributed blacklists

import redis
import json

class TokenBlacklist:
    def __init__(self, redis_client):
        self.redis = redis_client
        self.prefix = 'jwt_blacklist:'
    
    def blacklist_token(self, token, expiry_time=None):
        """Add token to blacklist"""
        try:
            # Extract token ID if available
            payload = jwt.decode(token, options={"verify_signature": False})
            token_id = payload.get('jti') or self._hash_token(token)
            
            # Calculate TTL from token expiry
            if not expiry_time:
                exp = payload.get('exp')
                if exp:
                    expiry_time = exp - int(time.time())
                else:
                    expiry_time = 3600  # Default 1 hour
            
            # Store in Redis with TTL
            self.redis.setex(
                f"{self.prefix}{token_id}",
                expiry_time,
                json.dumps({
                    'blacklisted_at': time.time(),
                    'reason': 'manual_revocation'
                })
            )
            
        except Exception as e:
            # Log error but don't fail
            print(f"Failed to blacklist token: {e}")
    
    def is_blacklisted(self, token):
        """Check if token is blacklisted"""
        try:
            payload = jwt.decode(token, options={"verify_signature": False})
            token_id = payload.get('jti') or self._hash_token(token)
            
            return self.redis.exists(f"{self.prefix}{token_id}")
            
        except Exception:
            # If we can't decode, consider it invalid
            return True
    
    def _hash_token(self, token):
        """Generate hash for tokens without JTI"""
        return hashlib.sha256(token.encode()).hexdigest()[:16]

5. Prevent Common Attacks:

# Timing attack prevention
def constant_time_compare(a, b):
    """Compare strings in constant time"""
    if len(a) != len(b):
        return False
    
    result = 0
    for x, y in zip(a, b):
        result |= ord(x) ^ ord(y)
    
    return result == 0

# JWT bombing prevention (large token payloads)
MAX_JWT_SIZE = 8192  # 8KB limit

def validate_jwt_size(token):
    if len(token) > MAX_JWT_SIZE:
        raise ValueError('JWT token too large')
    return True

# Prevent algorithm confusion attacks
def validate_algorithm_consistency(token, expected_algorithm):
    header = jwt.get_unverified_header(token)
    if header.get('alg') != expected_algorithm:
        raise jwt.InvalidAlgorithmError('Algorithm mismatch')
    return True`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Implementation</h2>
          
          <h3 style={styles.heading}>Complete Authentication System</h3>
          <p style={styles.paragraph}>
            A production-ready authentication system combines OAuth 2.0 and JWT with proper security 
            measures, user management, and integration patterns. Here's a comprehensive implementation 
            example.
          </p>
          <pre style={styles.codeBlock}>
{`Complete Authentication System:

# Authentication Service (Python/FastAPI)
from fastapi import FastAPI, HTTPException, Depends, status
from fastapi.security import HTTPBearer, HTTPAuthorizationCredentials
from pydantic import BaseModel
import bcrypt
import redis
from datetime import datetime, timedelta
import uuid

app = FastAPI()
security = HTTPBearer()
redis_client = redis.Redis(host='localhost', port=6379, db=0)

class AuthService:
    def __init__(self):
        self.jwt_manager = JWTManager(
            secret_key=os.getenv('JWT_SECRET'),
            private_key=os.getenv('JWT_PRIVATE_KEY'),
            public_key=os.getenv('JWT_PUBLIC_KEY')
        )
        self.token_blacklist = TokenBlacklist(redis_client)
        self.rate_limiter = RateLimiter()
    
    async def register_user(self, email: str, password: str, name: str) -> dict:
        """Register new user"""
        # Check if user exists
        if await self.get_user_by_email(email):
            raise HTTPException(
                status_code=status.HTTP_409_CONFLICT,
                detail="User already exists"
            )
        
        # Hash password
        password_hash = bcrypt.hashpw(password.encode(), bcrypt.gensalt())
        
        # Create user
        user_id = str(uuid.uuid4())
        user_data = {
            'id': user_id,
            'email': email,
            'name': name,
            'password_hash': password_hash.decode(),
            'roles': ['user'],
            'created_at': datetime.utcnow().isoformat(),
            'email_verified': False,
            'active': True
        }
        
        # Store user (implement your database logic)
        await self.store_user(user_data)
        
        # Send verification email
        await self.send_verification_email(user_id, email)
        
        return {'user_id': user_id, 'message': 'User registered successfully'}
    
    async def authenticate_user(self, email: str, password: str, client_ip: str) -> dict:
        """Authenticate user and return tokens"""
        # Rate limiting
        if not self.rate_limiter.is_allowed(client_ip, 'login'):
            raise HTTPException(
                status_code=status.HTTP_429_TOO_MANY_REQUESTS,
                detail="Too many login attempts"
            )
        
        # Get user
        user = await self.get_user_by_email(email)
        if not user or not user['active']:
            raise HTTPException(
                status_code=status.HTTP_401_UNAUTHORIZED,
                detail="Invalid credentials"
            )
        
        # Verify password
        if not bcrypt.checkpw(password.encode(), user['password_hash'].encode()):
            raise HTTPException(
                status_code=status.HTTP_401_UNAUTHORIZED,
                detail="Invalid credentials"
            )
        
        # Create tokens
        access_token = self.jwt_manager.create_access_token(
            user_id=user['id'],
            email=user['email'],
            roles=user['roles']
        )
        
        refresh_token = self.jwt_manager.create_refresh_token(user['id'])
        
        # Store refresh token
        await self.store_refresh_token(user['id'], refresh_token)
        
        # Log successful login
        await self.log_user_activity(user['id'], 'login', client_ip)
        
        return {
            'access_token': access_token,
            'refresh_token': refresh_token,
            'token_type': 'Bearer',
            'expires_in': 3600,
            'user': {
                'id': user['id'],
                'email': user['email'],
                'name': user['name'],
                'roles': user['roles']
            }
        }
    
    async def refresh_tokens(self, refresh_token: str) -> dict:
        """Refresh access token"""
        try:
            # Verify refresh token
            payload = self.jwt_manager.verify_token(refresh_token, 'refresh')
            user_id = payload['sub']
            
            # Check if refresh token is valid in database
            if not await self.is_refresh_token_valid(user_id, refresh_token):
                raise HTTPException(
                    status_code=status.HTTP_401_UNAUTHORIZED,
                    detail="Invalid refresh token"
                )
            
            # Get user data
            user = await self.get_user_by_id(user_id)
            if not user or not user['active']:
                raise HTTPException(
                    status_code=status.HTTP_401_UNAUTHORIZED,
                    detail="User not found or inactive"
                )
            
            # Create new tokens
            new_access_token = self.jwt_manager.create_access_token(
                user_id=user['id'],
                email=user['email'],
                roles=user['roles']
            )
            
            new_refresh_token = self.jwt_manager.create_refresh_token(user['id'])
            
            # Rotate refresh token
            await self.revoke_refresh_token(user_id, refresh_token)
            await self.store_refresh_token(user_id, new_refresh_token)
            
            return {
                'access_token': new_access_token,
                'refresh_token': new_refresh_token,
                'token_type': 'Bearer',
                'expires_in': 3600
            }
            
        except Exception as e:
            raise HTTPException(
                status_code=status.HTTP_401_UNAUTHORIZED,
                detail="Token refresh failed"
            )
    
    async def logout_user(self, access_token: str, refresh_token: str = None):
        """Logout user and revoke tokens"""
        try:
            # Blacklist access token
            self.token_blacklist.blacklist_token(access_token)
            
            # Revoke refresh token if provided
            if refresh_token:
                payload = self.jwt_manager.verify_token(refresh_token, 'refresh')
                user_id = payload['sub']
                await self.revoke_refresh_token(user_id, refresh_token)
            
            return {'message': 'Logged out successfully'}
            
        except Exception as e:
            # Log error but don't fail logout
            print(f"Logout error: {e}")
            return {'message': 'Logged out successfully'}

# Request/Response Models
class RegisterRequest(BaseModel):
    email: str
    password: str
    name: str

class LoginRequest(BaseModel):
    email: str
    password: str

class RefreshRequest(BaseModel):
    refresh_token: str

# API Endpoints
auth_service = AuthService()

@app.post("/auth/register")
async def register(request: RegisterRequest):
    return await auth_service.register_user(
        request.email, request.password, request.name
    )

@app.post("/auth/login")
async def login(request: LoginRequest, client_ip: str = Depends(get_client_ip)):
    return await auth_service.authenticate_user(
        request.email, request.password, client_ip
    )

@app.post("/auth/refresh")
async def refresh(request: RefreshRequest):
    return await auth_service.refresh_tokens(request.refresh_token)

@app.post("/auth/logout")
async def logout(credentials: HTTPAuthorizationCredentials = Depends(security)):
    token = credentials.credentials
    return await auth_service.logout_user(token)

# Authentication Middleware
async def get_current_user(credentials: HTTPAuthorizationCredentials = Depends(security)):
    token = credentials.credentials
    
    try:
        payload = auth_service.jwt_manager.verify_token(token)
        
        # Check if token is blacklisted
        if auth_service.token_blacklist.is_blacklisted(token):
            raise HTTPException(
                status_code=status.HTTP_401_UNAUTHORIZED,
                detail="Token has been revoked"
            )
        
        return {
            'id': payload['sub'],
            'email': payload['email'],
            'roles': payload['roles']
        }
        
    except Exception as e:
        raise HTTPException(
            status_code=status.HTTP_401_UNAUTHORIZED,
            detail="Invalid token"
        )

# Protected Routes
@app.get("/api/profile")
async def get_profile(current_user: dict = Depends(get_current_user)):
    return {
        'user_id': current_user['id'],
        'email': current_user['email'],
        'roles': current_user['roles']
    }

@app.get("/api/admin/users")
async def get_users(current_user: dict = Depends(get_current_user)):
    if 'admin' not in current_user['roles']:
        raise HTTPException(
            status_code=status.HTTP_403_FORBIDDEN,
            detail="Admin access required"
        )
    
    # Return users list
    return await get_all_users()

# Frontend Integration (React)
class AuthProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            loading: true,
            accessToken: null
        };
    }

    async componentDidMount() {
        await this.checkAuthStatus();
    }

    async checkAuthStatus() {
        const refreshToken = this.getRefreshToken();
        if (refreshToken) {
            try {
                await this.refreshAccessToken();
            } catch (error) {
                this.logout();
            }
        }
        this.setState({ loading: false });
    }

    async login(email, password) {
        try {
            const response = await fetch('/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                throw new Error('Login failed');
            }

            const data = await response.json();
            
            // Store tokens
            this.setAccessToken(data.access_token);
            this.setRefreshToken(data.refresh_token);
            
            this.setState({
                user: data.user,
                accessToken: data.access_token
            });

            return data.user;
        } catch (error) {
            throw error;
        }
    }

    async refreshAccessToken() {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token');
        }

        const response = await fetch('/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        this.setAccessToken(data.access_token);
        this.setRefreshToken(data.refresh_token);
        
        this.setState({ accessToken: data.access_token });
    }

    async logout() {
        try {
            await fetch('/auth/logout', {
                method: 'POST',
                headers: {
                    'Authorization': \`Bearer $\{this.state.accessToken\}\`
                }
            });
        } catch (error) {
            console.error('Logout error:', error);
        }

        this.clearTokens();
        this.setState({
            user: null,
            accessToken: null
        });
    }

    setAccessToken(token) {
        // Store in memory only
        this.accessToken = token;
    }

    setRefreshToken(token) {
        // Store in httpOnly cookie (server-side)
        document.cookie = \`refresh_token=$\{token\}; HttpOnly; Secure; SameSite=Strict; Max-Age=2592000\`;
    }

    clearTokens() {
        this.accessToken = null;
        document.cookie = 'refresh_token=; HttpOnly; Secure; SameSite=Strict; Max-Age=0';
    }

    render() {
        return (
            <AuthContext.Provider value={{
                user: this.state.user,
                loading: this.state.loading,
                login: this.login.bind(this),
                logout: this.logout.bind(this),
                getAccessToken: () => this.state.accessToken
            }}>
                {this.props.children}
            </AuthContext.Provider>
        );
    }
}`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Use HTTPS everywhere:</strong> Never transmit tokens or credentials over 
              unencrypted connections in production environments.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper token lifecycle management:</strong> Use short-lived access 
              tokens, secure refresh token rotation, and token blacklisting for revocation.
            </li>
            <li style={styles.listItem}>
              <strong>Follow the principle of least privilege:</strong> Grant minimal scopes and 
              permissions necessary for the application to function.
            </li>
            <li style={styles.listItem}>
              <strong>Validate all inputs and claims:</strong> Never trust client-provided data 
              and always validate token claims on the server side.
            </li>
            <li style={styles.listItem}>
              <strong>Implement comprehensive logging and monitoring:</strong> Track authentication 
              events, failed attempts, and suspicious activities for security analysis.
            </li>
            <li style={styles.listItem}>
              <strong>Keep libraries and dependencies updated:</strong> Regularly update OAuth and 
              JWT libraries to patch security vulnerabilities.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            OAuth 2.0 and JWT provide powerful, standardized approaches to authentication and authorization 
            in modern applications. OAuth 2.0 handles the authorization flow and token issuance, while JWT 
            provides a secure, stateless format for representing user identity and permissions.
          </p>
          <p style={styles.paragraph}>
            Success with these technologies requires understanding the security implications, implementing 
            proper validation and token management, and following established best practices. When implemented 
            correctly, they enable scalable, secure authentication systems that can handle millions of users 
            across distributed architectures.
          </p>
        </section>
      </main>
    </article>
  );
};

export default OAuthJWT;
